import axios from 'axios';
import { isDesktop } from '../utils/desktop';

/**
 * A versão desktop (Neutralino) é servida por um servidor local, então
 * window.location.hostname é SEMPRE 'localhost'. Sem a checagem de isDesktop(),
 * a heurística abaixo conclui "estou em desenvolvimento" e aponta a API para
 * http://localhost:3001 — que não existe na máquina da loja.
 *
 * O sintoma engana: o navegador reporta falha de rede no preflight e parece
 * bloqueio de CORS, quando na verdade é conexão recusada a um servidor de
 * desenvolvimento ausente. O CORS da API já aceita qualquer porta local
 * (ver isDesktopApp em proxy-server-vps.js).
 */
const ehDesenvolvimentoLocal =
  !isDesktop() && ['localhost', '127.0.0.1'].includes(window.location.hostname);

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  ehDesenvolvimentoLocal
    ? 'http://localhost:3001/api'
    : 'https://api.seellbr.com/vendascall/api'
);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const PROXY_API_URL = process.env.REACT_APP_PROXY_URL || 'https://api.seellbr.com/api';
const apiProxy = axios.create({
  baseURL: PROXY_API_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const INVENTARIO_PROXY_API_URL = process.env.REACT_APP_PROXY_INVENTARIO_URL || PROXY_API_URL;
const apiInventarioProxy = axios.create({
  baseURL: INVENTARIO_PROXY_API_URL,
  timeout: 30000
});

const legacyApi = axios.create({
  baseURL: window.location.origin,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

function addAuthToken(config) {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}

api.interceptors.request.use(addAuthToken, (e) => Promise.reject(e));
apiProxy.interceptors.request.use(addAuthToken, (e) => Promise.reject(e));
apiInventarioProxy.interceptors.request.use(addAuthToken, (e) => Promise.reject(e));

function handleUnauthorized(error) {
  if (error.response?.status === 401) {
    localStorage.removeItem('token');
    if (error.config?.url?.includes('/auth/verify-remember-token')) {
      localStorage.removeItem('rememberToken');
    }
  }
  return Promise.reject(error);
}

api.interceptors.response.use((r) => r, handleUnauthorized);
apiProxy.interceptors.response.use((r) => r, handleUnauthorized);
apiInventarioProxy.interceptors.response.use((r) => r, handleUnauthorized);

// Serviço de autenticação
export const authService = {
  login: async (usuario, senha, lembrar = false) => {
    const response = await api.post('/auth/login', {
      usuario,
      senha,
      lembrar
    });
    if (response.data?.status === 'sucesso' && response.data?.token) {
      localStorage.setItem('token', response.data.token);
      if (response.data.rememberToken) {
        localStorage.setItem('rememberToken', response.data.rememberToken);
      } else {
        localStorage.removeItem('rememberToken');
      }
    }
    return response.data;
  },

  logout: async () => {
    const rememberToken = localStorage.getItem('rememberToken');
    try {
      const response = await api.post('/auth/logout', { rememberToken });
      return response.data;
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('rememberToken');
    }
  },

  me: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  verifyRememberToken: async (token) => {
    const response = await api.post('/auth/verify-remember-token', { token });
    if (response.data?.status === 'sucesso' && response.data?.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },

  checkAuth: async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await api.get('/auth/me');
        if (response.data?.status === 'sucesso' && response.data?.usuario) {
          return { autenticado: true, usuario: response.data.usuario };
        }
      } catch {
        localStorage.removeItem('token');
      }
    }

    const rememberToken = localStorage.getItem('rememberToken');
    if (rememberToken) {
      try {
        const response = await api.post('/auth/verify-remember-token', { token: rememberToken });
        if (response.data?.status === 'sucesso' && response.data?.usuario) {
          if (response.data.token) localStorage.setItem('token', response.data.token);
          return { autenticado: true, usuario: response.data.usuario };
        }
      } catch {
        localStorage.removeItem('rememberToken');
      }
    }

    return { autenticado: false };
  }
};

// Serviço de lojas
export const lojasService = {
  getLojas: async () => {
    const response = await api.get('/lojas');
    if (response.data?.status === 'success' && Array.isArray(response.data.lojas)) {
      return response.data.lojas;
    }
    return [];
  }
};

// Serviço de queries CallFarma
export const queryService = {
  execute: async (query) => {
    const response = await apiProxy.post('/query', { query });
    if (response.data && response.data.success && response.data.data && response.data.data.status) {
      return response.data.data.data;
    }
    return [];
  }
};

export const preContagemBalancoService = {
  async buscarProduto(codigoLido, filial = 22) {
    const codigo = String(codigoLido || '').trim();
    if (!codigo) throw new Error('Informe um código');

    const [data, estoqueFilial] = await Promise.all([
      produtoService.buscarPreco(codigo, filial),
      produtoService.buscarEstoqueFilialPorCodigoLido(codigo, filial).catch(() => null)
    ]);

    if (!data?.cdprodu && !data?.produto?.CDPRODU) {
      return null;
    }

    return {
      CDPRODU: data.cdprodu || data.produto?.CDPRODU,
      NOME: data.produto?.NOME || data.nome || 'Produto sem descrição',
      BARRA: data.produto?.BARRA || '',
      BAR1: data.produto?.BAR1 || '',
      BAR2: data.produto?.BAR2 || '',
      BAR3: data.produto?.BAR3 || '',
      BAR4: data.produto?.BAR4 || '',
      ESTOQUE_FILIAL: estoqueFilial?.ESTOQUE ?? null,
      ENDERECO_SISTEMA: estoqueFilial?.ENDERECO || estoqueFilial?.ENDER || data?.detalhes?.endereco || ''
    };
  }
};

export const caixasService = {
  buscarCaixasPendentes: async (filial) => {
    const filialNum = parseInt(filial, 10);
    const schemas = ['estwin.', 'estwin950.', ''];
    const resultado = [];

    const montarQuery = (schema) => `
      SELECT
        a.NRPEDIDO,
        a.CDFIL,
        a.CDFILD,
        a.CDEMP,
        a.DATA,
        b.CDCAIXA AS CAIXA,
        b.DATARECFIL,
        b.HORARECFIL,
        b.DATACONFFIL,
        b.HORACONFFIL,
        b.OPERSCAN,
        b.HORASCAN,
        b.DATASCAN,
        b.STATUS_BAIXA,
        b.STATUS,
        a.FGSTATUS,
        DATEDIFF(CURDATE(), a.DATA) AS DIAS_PENDENTE
      FROM ${schema}scepedc a
      JOIN ${schema}scepedcaixa b ON a.NRPEDIDO = b.NRPEDIDO
      WHERE a.CDFILD = ${filialNum}
        AND DATE_FORMAT(a.DATA,'%Y/%m/%d') >= DATE_FORMAT(DATE_SUB(CURDATE(), INTERVAL 30 DAY),'%Y/%m/%d')
        AND DATE_FORMAT(a.DATA,'%Y/%m/%d') <= DATE_FORMAT(CURDATE(),'%Y/%m/%d')
        AND ((a.FGSTATUS = 3) OR (b.DATARECFIL <> '' AND b.STATUS_BAIXA = 'A'))
        AND b.STATUS <> 'E'
        AND b.STATUS <> 'D'
      ORDER BY a.DATA ASC, a.NRPEDIDO ASC, b.CDCAIXA ASC
    `;

    for (const schema of schemas) {
      try {
        const rows = await queryService.execute(montarQuery(schema));
        if (Array.isArray(rows) && rows.length > 0) {
          resultado.push(...rows);
        }
      } catch (_) {
        // Ignora schema sem permissão/tabela e segue.
      }
    }

    const filtrado = resultado.filter((c) => String(c.STATUS_BAIXA || '').toUpperCase() === 'A');
    const vistos = new Set();
    const dedup = [];
    for (const row of filtrado) {
      const key = `${row.CDEMP || ''}|${row.CDFIL || ''}|${row.CDFILD || ''}|${row.NRPEDIDO || ''}|${row.CAIXA || ''}`;
      if (!vistos.has(key)) {
        vistos.add(key);
        dedup.push(row);
      }
    }

    dedup.sort((a, b) => {
      const da = new Date(a.DATA).getTime() || 0;
      const db = new Date(b.DATA).getTime() || 0;
      if (da !== db) return da - db;
      if (String(a.NRPEDIDO) !== String(b.NRPEDIDO)) return String(a.NRPEDIDO).localeCompare(String(b.NRPEDIDO));
      return String(a.CAIXA).localeCompare(String(b.CAIXA));
    });

    return dedup;
  },

  buscarProdutosDaCaixa: async ({ filial, pedido, caixa, cdemp = 1, filialDestino = null }) => {
    const pedidoStr = String(pedido);
    const caixaStr = String(caixa);
    const cdempStr = String(cdemp);
    const filialStr = filial != null ? String(filial) : null;
    const filialDestinoStr = filialDestino != null ? String(filialDestino) : null;

    const montarQueryPedido = (schema) => {
      const filtroDestino = filialDestinoStr ? ` AND CDFILD = '${filialDestinoStr}'` : '';
      return `
        SELECT CDFIL, CDFILD
        FROM ${schema}scepedc
        WHERE CDEMP = '${cdempStr}'
          AND NRPEDIDO = '${pedidoStr}'
          ${filtroDestino}
        LIMIT 20
      `;
    };

    const schemas = ['estwin.', 'estwin950.', ''];
    const filiaisTentativa = [];

    if (filialStr) filiaisTentativa.push(filialStr);

    for (const schema of schemas) {
      try {
        const pedRows = await queryService.execute(montarQueryPedido(schema));
        if (Array.isArray(pedRows) && pedRows.length > 0) {
          pedRows.forEach((r) => {
            if (r.CDFIL != null) filiaisTentativa.push(String(r.CDFIL));
            if (r.CDFILD != null) filiaisTentativa.push(String(r.CDFILD));
          });
        }
      } catch (_) {
        // Ignora erro de schema e segue para o próximo
      }
    }

    const filiaisUnicas = [...new Set(filiaisTentativa.filter(Boolean))];

    const montarQueryProdutos = (schema, cdfil) => `
      SELECT
        x.*,
        p.NOME AS NOMEPRO,
        p.QTDEXP,
        p.EMBEXP,
        p.BARRA,
        p.BAR1,
        p.BAR2
      FROM ${schema}scepedicx x
      JOIN ${schema}sceprodu p ON x.CDPRODU = p.CDPRODU
      WHERE x.CDFIL = '${cdfil}'
        AND x.CDEMP = '${cdempStr}'
        AND x.NRPEDIDO = '${pedidoStr}'
        AND x.CAIXA = '${caixaStr}'
      ORDER BY p.NOME
    `;

    for (const schema of schemas) {
      for (const cdfil of filiaisUnicas) {
        const rows = await queryService.execute(montarQueryProdutos(schema, cdfil));
        if (Array.isArray(rows) && rows.length > 0) {
          return rows;
        }
      }
    }

    return [];
  },

  buscarEstoqueProdutos: async (filial, codigos = []) => {
    if (!filial || !Array.isArray(codigos) || codigos.length === 0) {
      return [];
    }

    const codigosLimpos = [...new Set(
      codigos
        .map((c) => parseInt(String(c), 10))
        .filter((c) => Number.isFinite(c))
    )];

    if (codigosLimpos.length === 0) return [];

    const query = `
      SELECT
        CDFIL,
        CDPRODU,
        COALESCE(ESTOQ, 0) AS ESTOQ
      FROM sceestoq
      WHERE CDFIL = ${parseInt(filial, 10)}
        AND CDPRODU IN (${codigosLimpos.join(',')})
    `;

    return queryService.execute(query);
  },

  confereCaixa: async ({ caixa, filial, matricula }) => {
    const caixaStr = String(caixa || '').trim();
    const filialStr = String(filial || '').trim();
    const matriculaStr = String(matricula || '').trim();

    if (!caixaStr) throw new Error('Informe a caixa');
    if (!filialStr) throw new Error('Informe a filial');
    if (!matriculaStr) throw new Error('Informe a matrícula');

    const response = await apiProxy.post('/proxy/estoque/caixa/confere', {
      caixa: caixaStr,
      filial: filialStr,
      matricula: matriculaStr
    });

    if (response.data?.success) return response.data;
    throw new Error(response.data?.error || 'Erro ao conferir caixa');
  }
};

export const transferenciasService = {
  listarPedidosPendentes: async ({ filialRetira, dataIniYmd, dataFimYmd }) => {
    const query = `
      SELECT
        a.pedido,
        a.codcorreio,
        a.parceiro_nome,
        a.data,
        a.hora,
        a.cliente,
        b.nome,
        a.STATUS,
        a.ENTREGA,
        a.FRETE,
        sum(c.VALOR * c.qtd) as VLRTOTAL,
        a.FORMAPG,
        a.TIPOPED,
        a.DTENTREGA,
        a.DATAEFET,
        a.HORAEFET,
        a.NRPEDTELE,
        a.CONDPAG,
        a.TEMRECEITA,
        a.DATAIMP,
        a.OPERIMP,
        a.OPERRECUSA,
        a.DATARECUSA,
        a.HORARECUSA
      FROM televendas.admped1 a, televendas.pdvcliente b, televendas.admped2 c
      WHERE (a.STATUS < 2 OR a.STATUS = 7)
        AND CONCAT(MID(a.data,7,4),MID(a.data,4,2),MID(a.data,1,2)) >= '${dataIniYmd}'
        AND CONCAT(MID(a.data,7,4),MID(a.data,4,2),MID(a.data,1,2)) <= '${dataFimYmd}'
        AND a.PEDIDO = c.PEDIDO
        AND (a.FORMAPG = 1 OR a.FORMAPG = 4 OR a.FORMAPG = 5 OR a.FORMAPG = 8 OR a.FORMAPG = 9 OR a.FORMAPG = 10 OR a.FORMAPG = 11)
        AND a.CLIENTE = b.CODIGO
        AND FILIALRETIRA = ${parseInt(filialRetira, 10)}
      GROUP BY a.PEDIDO
      ORDER BY a.PEDIDO
    `;
    return queryService.execute(query);
  },

  listarSolicitacoesTransferenciaPendentes: async ({ filialOrigemTransf, dataIniYmd, dataFimYmd }) => {
    const query = `
      SELECT
        a.CUPOM as pedido,
        a.LOJA,
        a.data,
        a.hora,
        a.cliente,
        b.nome,
        a.STATUS,
        a.ENTREGA,
        sum(c.VLRUNI * c.qtd) as VLRTOTAL,
        a.FORMAPG,
        a.DTPENTREGA,
        a.DATA as DATAEFET,
        a.HORA as HORAEFET
      FROM televendas.pdvcupom a, televendas.pdvcliente b, televendas.pdvitem c
      WHERE a.FEZTRANSFERENCIA = 'N'
        AND filialvenda <> ${parseInt(filialOrigemTransf, 10)}
        AND CONCAT(MID(a.DATA,7,4),MID(a.DATA,4,2),MID(a.DATA,1,2)) >= '${dataIniYmd}'
        AND CONCAT(MID(a.DATA,7,4),MID(a.DATA,4,2),MID(a.DATA,1,2)) <= '${dataFimYmd}'
        AND a.CUPOM = c.CUPOM
        AND a.CLIENTE = b.CODIGO
        AND ENTREGA = 'ENTREGAEMCASA'
        AND FILIALORIGENTRANSF = ${parseInt(filialOrigemTransf, 10)}
        AND FILIALORIGENTRANSF <> LOJA
      GROUP BY a.CUPOM
      ORDER BY a.CUPOM
    `;
    return queryService.execute(query);
  },

  listarClienteRetiraTransfNaoFeita: async ({ lojaTransf, dataIniYmd, dataFimYmd }) => {
    const query = `
      SELECT
        a.NRNFCE as pedido,
        a.data,
        a.hora,
        a.cpf,
        a.QTD,
        a.CDFIL,
        c.NOME as NOMEPRO
      FROM estwin.clienteretira a, estwin.sceprodu c
      WHERE a.LOJATRANSF = ${parseInt(lojaTransf, 10)}
        AND a.FEZTRANSFERENCIA = 'N'
        AND CONCAT(MID(a.DATA,7,4),MID(a.DATA,4,2),MID(a.DATA,1,2)) >= '${dataIniYmd}'
        AND CONCAT(MID(a.DATA,7,4),MID(a.DATA,4,2),MID(a.DATA,1,2)) <= '${dataFimYmd}'
        AND a.LJENTREGA <> a.LOJATRANSF
        AND a.CDPRODU = c.CDPRODU
      ORDER BY a.NRNFCE
    `;
    return queryService.execute(query);
  },

  listarClienteRetiraPendenteEntrega: async ({ lojaEntrega, dataIniYmd, dataFimYmd }) => {
    const query = `
      SELECT
        a.ID as pedido,
        a.data,
        a.hora,
        a.cpf,
        a.QTD,
        a.CDFIL,
        c.NOME as NOMEPRO,
        a.LJENTREGA
      FROM estwin.clienteretira a, estwin.sceprodu c
      WHERE a.QTD > a.QTDENTREGUE
        AND a.LJENTREGA <> a.CDFIL
        AND a.LJENTREGA = ${parseInt(lojaEntrega, 10)}
        AND CONCAT(MID(a.DATA,7,4),MID(a.DATA,4,2),MID(a.DATA,1,2)) >= '${dataIniYmd}'
        AND CONCAT(MID(a.DATA,7,4),MID(a.DATA,4,2),MID(a.DATA,1,2)) <= '${dataFimYmd}'
        AND a.CDPRODU = c.CDPRODU
      ORDER BY a.NRNFCE
    `;
    return queryService.execute(query);
  },

  listarMovTransferencia: async ({ cdfil, dataIniYmdSlash, dataFimYmdSlash, tipo }) => {
    const extraTipo =
      tipo === 'OUTROS'
        ? "AND TIPO <> 'R' AND TIPO <> 'P'"
        : tipo === 'R'
          ? "AND TIPO = 'R' AND MARCADOPARAAPAGAR <> 'S' GROUP BY a.DOC"
          : tipo === 'P'
            ? "AND TIPO = 'P' GROUP BY a.DOC"
            : '';

    const orderBy =
      tipo === 'R'
        ? 'ORDER BY a.CDFILDEST, a.DOC'
        : 'ORDER BY a.DOC';

    const query = `
      SELECT a.*, c.NOME as NOMEPRO
      FROM estwin.scemov_transferencia a, estwin.sceprodu c
      WHERE a.CDFIL = ${parseInt(cdfil, 10)}
        AND (DATE_FORMAT(a.DATA,'%Y/%m/%d') >= '${dataIniYmdSlash}')
        AND (DATE_FORMAT(a.DATA,'%Y/%m/%d') <= '${dataFimYmdSlash}')
        AND a.CDPRODU = c.CDPRODU
        ${extraTipo}
      ${orderBy}
    `;
    return queryService.execute(query);
  },

  // Detalhes para a impressão de recebimento em WindowTransferenciasPendentes,
  // que lista REMANEJAMENTO (TIPO='R', rotina automática do estwin). Não confundir
  // com a tela de Transferência de Mercadoria, que opera TIPO='C' (Depto Compras).
  listarMovTransferenciaDetalhesRecebimento: async ({ cdfil, docs }) => {
    const docsValidos = Array.isArray(docs)
      ? docs.map((doc) => parseInt(doc, 10)).filter((doc) => Number.isFinite(doc))
      : [];

    if (docsValidos.length === 0) return [];

    const query = `
      SELECT
        a.DOC,
        a.CDFILDEST,
        f.ABREV as FILIAL_DEST_NOME,
        a.CDPRODU,
        a.QTD,
        p.BARRA,
        p.BAR1,
        p.BAR2,
        p.NOME as NOMEPRO,
        COALESCE(e.ENDER, '') as ENDERECO
      FROM estwin.scemov_transferencia a
      LEFT JOIN estwin.sceprodu p ON p.CDPRODU = a.CDPRODU
      LEFT JOIN estwin.sceestoq e ON e.CDPRODU = a.CDPRODU AND e.CDFIL = a.CDFIL
      LEFT JOIN estwin.scefilial f ON f.CDFIL = a.CDFILDEST
      WHERE a.CDFIL = ${parseInt(cdfil, 10)}
        AND a.TIPO = 'R'
        AND a.MARCADOPARAAPAGAR <> 'S'
        AND a.DOC IN (${docsValidos.join(',')})
      ORDER BY a.DOC, a.CDFILDEST, p.NOME, a.CDPRODU
    `;

    return queryService.execute(query);
  }
};

export const cuponsService = {
  listarCupons: async ({ cupom = '', filial = '', dataIni = '', dataFim = '', valorMin = '', valorMax = '', formaPagamento = '' }) => {
    const cupomLimpo = String(cupom || '').trim();
    const buscaPorCupom = cupomLimpo.length > 0;
    const filialNum = String(filial || '').trim() ? parseInt(filial, 10) : null;
    const formaPagamentoNum = String(formaPagamento || '').trim() ? parseInt(formaPagamento, 10) : null;
    const filtroCupom = buscaPorCupom ? ` AND k.DOC = '${cupomLimpo.replace(/'/g, "''")}'` : '';
    const filtroFilial = Number.isFinite(filialNum) ? ` AND k.CDFIL = ${filialNum}` : '';
    const filtroDataIni = !buscaPorCupom && dataIni ? ` AND k.DATA >= '${String(dataIni)}'` : '';
    const filtroDataFim = !buscaPorCupom && dataFim ? ` AND k.DATA <= '${String(dataFim)}'` : '';

    let filtroFormaPagamento = '';
    if (Number.isFinite(formaPagamentoNum)) {
      const filtroPgCupom = buscaPorCupom ? ` AND DOCUMENTO = '${cupomLimpo.replace(/'/g, "''")}'` : '';
      const filtroPgFilial = Number.isFinite(filialNum) ? ` AND LOJA = ${filialNum}` : '';
      const filtroPgDataIni = !buscaPorCupom && dataIni
        ? ` AND (
            DATA >= '${String(dataIni)}'
            OR DATA_CAIXA >= '${String(dataIni)}'
            OR DATE_FORMAT(DATA, '%Y-%m-%d') >= '${String(dataIni)}'
            OR DATE_FORMAT(DATA_CAIXA, '%Y-%m-%d') >= '${String(dataIni)}'
            OR DATE_FORMAT(STR_TO_DATE(DATA, '%d/%m/%Y'), '%Y-%m-%d') >= '${String(dataIni)}'
            OR DATE_FORMAT(STR_TO_DATE(DATA_CAIXA, '%d/%m/%Y'), '%Y-%m-%d') >= '${String(dataIni)}'
          )`
        : '';
      const filtroPgDataFim = !buscaPorCupom && dataFim
        ? ` AND (
            DATA <= '${String(dataFim)}'
            OR DATA_CAIXA <= '${String(dataFim)}'
            OR DATE_FORMAT(DATA, '%Y-%m-%d') <= '${String(dataFim)}'
            OR DATE_FORMAT(DATA_CAIXA, '%Y-%m-%d') <= '${String(dataFim)}'
            OR DATE_FORMAT(STR_TO_DATE(DATA, '%d/%m/%Y'), '%Y-%m-%d') <= '${String(dataFim)}'
            OR DATE_FORMAT(STR_TO_DATE(DATA_CAIXA, '%d/%m/%Y'), '%Y-%m-%d') <= '${String(dataFim)}'
          )`
        : '';

      const pagamentos = await queryService.execute(`
        SELECT DISTINCT
          DOCUMENTO,
          LOJA,
          COALESCE(NULLIF(DATA_CAIXA, ''), DATA) AS DATA
        FROM financall.admpagamentodetalhe
        WHERE FORMAPG = ${formaPagamentoNum}
          ${filtroPgCupom}
          ${filtroPgFilial}
          ${filtroPgDataIni}
          ${filtroPgDataFim}
        ORDER BY DATA DESC, DOCUMENTO DESC
        LIMIT 500
      `);

      if (!Array.isArray(pagamentos) || pagamentos.length === 0) return [];

      const normalizarData = (value) => {
        const str = String(value || '').trim().slice(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
          const [d, m, y] = str.split('/');
          return `${y}-${m}-${d}`;
        }
        return str;
      };

      const condicoesPg = pagamentos
        .map((item) => {
          const doc = String(item.DOCUMENTO || '').trim().replace(/'/g, "''");
          const loja = parseInt(item.LOJA, 10);
          const dataPg = normalizarData(item.DATA).replace(/'/g, "''");
          if (!doc || !Number.isFinite(loja)) return '';
          return dataPg
            ? `(k.DOC = '${doc}' AND k.CDFIL = ${loja} AND k.DATA = '${dataPg}')`
            : `(k.DOC = '${doc}' AND k.CDFIL = ${loja})`;
        })
        .filter(Boolean);

      if (!condicoesPg.length) return [];
      filtroFormaPagamento = ` AND (${condicoesPg.join(' OR ')})`;
    }

    const valorMinNum = !buscaPorCupom && String(valorMin || '').trim() ? parseFloat(String(valorMin).replace(',', '.')) : null;
    const valorMaxNum = !buscaPorCupom && String(valorMax || '').trim() ? parseFloat(String(valorMax).replace(',', '.')) : null;
    const having = [];
    if (Number.isFinite(valorMinNum)) having.push(`SUM(k.VALOR) >= ${valorMinNum}`);
    if (Number.isFinite(valorMaxNum)) having.push(`SUM(k.VALOR) <= ${valorMaxNum}`);
    const havingSql = having.length ? ` HAVING ${having.join(' AND ')}` : '';

    const query = `
      SELECT
        k.DOC as CUPOM,
        k.CDFIL as LOJA,
        k.DATA as DATA,
        k.HORA,
        '' as CLIENTE,
        '' as NOME_CLIENTE,
        '' as STATUS,
        '' as ENTREGA,
        SUM(k.VALOR) as VLRTOTAL,
        SUM(k.QTD) as QTDITENS
      FROM scekarde k
      WHERE k.TIPO IN ('VE', 'DV')
        ${filtroCupom}
        ${filtroFilial}
        ${filtroDataIni}
        ${filtroDataFim}
        ${filtroFormaPagamento}
      GROUP BY k.DOC, k.CDFIL, k.DATA, k.HORA
      ${havingSql}
      ORDER BY k.DATA DESC, k.HORA DESC, k.DOC DESC
      LIMIT 300
    `;

    return queryService.execute(query);
  },

  listarProdutosCupom: async ({ cupom, filial = '', data = '', hora = '' }) => {
    const cupomLimpo = String(cupom || '').trim();
    if (!cupomLimpo) return [];

    const filialNum = String(filial || '').trim() ? parseInt(filial, 10) : null;
    const filtroFilial = Number.isFinite(filialNum) ? ` AND k.CDFIL = ${filialNum}` : '';
    const filtroData = data ? ` AND k.DATA = '${String(data).slice(0, 10)}'` : '';
    const filtroHora = String(hora || '').trim() ? ` AND k.HORA = '${String(hora).replace(/'/g, "''")}'` : '';

    const query = `
      SELECT
        k.DOC as CUPOM,
        k.CDPRODU,
        p.NOME,
        p.BARRA,
        p.BAR1,
        p.BAR2,
        SUM(k.QTD) as QTD,
        CASE WHEN SUM(k.QTD) = 0 THEN 0 ELSE SUM(k.VALOR) / SUM(k.QTD) END as VLRUNI,
        SUM(k.VALOR) as VLRTOTAL
      FROM scekarde k
      LEFT JOIN sceprodu p ON p.CDPRODU = k.CDPRODU
      WHERE k.TIPO IN ('VE', 'DV')
        AND k.DOC = '${cupomLimpo.replace(/'/g, "''")}'
        ${filtroFilial}
        ${filtroData}
        ${filtroHora}
      GROUP BY k.DOC, k.CDPRODU, p.NOME, p.BARRA, p.BAR1, p.BAR2
      ORDER BY p.NOME, k.CDPRODU
    `;

    return queryService.execute(query);
  },

  listarClienteCupom: async ({ cupom, filial = '', data = '', hora = '' }) => {
    const cupomLimpo = String(cupom || '').trim();
    if (!cupomLimpo) return null;

    const filialNum = String(filial || '').trim() ? parseInt(filial, 10) : null;
    const filtroFilial = Number.isFinite(filialNum) ? ` AND k.CDFIL = ${filialNum}` : '';
    const filtroDataKarde = data ? ` AND k.DATA = '${String(data).slice(0, 10).replace(/'/g, "''")}'` : '';
    const filtroHora = String(hora || '').trim() ? ` AND k.HORA = '${String(hora).replace(/'/g, "''")}'` : '';
    const filtroDataCupom = data
      ? ` AND (a.DATA = '${String(data).slice(0, 10).replace(/'/g, "''")}' OR DATE_FORMAT(STR_TO_DATE(a.DATA, '%d/%m/%Y'), '%Y-%m-%d') = '${String(data).slice(0, 10).replace(/'/g, "''")}')`
      : '';

    const query = `
      SELECT
        base.*,
        COALESCE(codigoCliente.NOME, cpfCliente.NOME, '') as NOME_CLIENTE,
        COALESCE(NULLIF(base.CPF_KARDE, ''), codigoCliente.CPF, cpfCliente.CPF, codigoCliente.CPF_LIMPO, cpfCliente.CPF_LIMPO, '') as CPF_CLIENTE
      FROM (
        SELECT
          k.DOC as DOCUMENTO,
          k.CDFIL as LOJA,
          k.DATA,
          k.HORA,
          MAX(NULLIF(k.nrCPF, '')) as CPF_KARDE,
          MAX(a.CLIENTE) as CLIENTE
        FROM scekarde k
        LEFT JOIN televendas.pdvcupom a
          ON a.CUPOM = k.DOC
         AND a.LOJA = k.CDFIL
         ${filtroDataCupom}
        WHERE k.TIPO IN ('VE', 'DV')
          AND k.DOC = '${cupomLimpo.replace(/'/g, "''")}'
          ${filtroFilial}
          ${filtroDataKarde}
          ${filtroHora}
        GROUP BY k.DOC, k.CDFIL, k.DATA, k.HORA
        LIMIT 1
      ) base
      LEFT JOIN televendas.pdvcliente codigoCliente ON codigoCliente.CODIGO = base.CLIENTE
      LEFT JOIN televendas.pdvcliente cpfCliente
        ON REPLACE(REPLACE(REPLACE(COALESCE(cpfCliente.CPF, ''), '.', ''), '-', ''), '/', '') = REPLACE(REPLACE(REPLACE(COALESCE(base.CPF_KARDE, ''), '.', ''), '-', ''), '/', '')
        OR CAST(cpfCliente.CPF_LIMPO AS CHAR) = REPLACE(REPLACE(REPLACE(COALESCE(base.CPF_KARDE, ''), '.', ''), '-', ''), '/', '')
      LIMIT 1
    `;

    const rows = await queryService.execute(query);
    return Array.isArray(rows) && rows.length ? rows[0] : null;
  },

  listarPagamentosCupons: async ({ cupons = [], filial = '' }) => {
    const pares = Array.isArray(cupons)
      ? cupons
        .map((item) => ({
          documento: String(item?.documento || '').trim(),
          loja: String(item?.loja || '').trim()
        }))
        .filter((item) => item.documento)
      : [];

    if (!pares.length) return [];

    const docsSql = [...new Set(pares.map((item) => item.documento))]
      .map((doc) => `'${doc.replace(/'/g, "''")}'`)
      .join(',');
    const lojasSql = [...new Set(pares.map((item) => parseInt(item.loja, 10)).filter(Number.isFinite))].join(',');
    const filialNum = String(filial || '').trim() ? parseInt(filial, 10) : null;
    const filtroLojas = lojasSql
      ? ` AND LOJA IN (${lojasSql})`
      : Number.isFinite(filialNum)
        ? ` AND LOJA = ${filialNum}`
        : '';

    const query = `
      SELECT
        DOCUMENTO,
        LOJA,
        DATA,
        FORMAPG,
        BANDEIRA,
        PARCELAS,
        VALOR,
        NSU,
        TID,
        PAYMENTID,
        STATUS_SITE,
        NRAUTORIZACAO
      FROM financall.admpagamentodetalhe
      WHERE DOCUMENTO IN (${docsSql})
        ${filtroLojas}
      ORDER BY LOJA, DATA, DOCUMENTO, REGID
    `;

    return queryService.execute(query);
  }
};

export const clienteretiraService = {
  listar: async ({ cdfil, dataIniYmd, dataFimYmd, vendedor = '', tipoRetirada = 'todas' }) => {
    const filialNum = parseInt(cdfil, 10);
    if (!Number.isFinite(filialNum)) throw new Error('Filial inválida');

    const vendedorNum = String(vendedor || '').trim() ? parseInt(String(vendedor).trim(), 10) : null;
    if (String(vendedor || '').trim() && !Number.isFinite(vendedorNum)) {
      throw new Error('Vendedor inválido');
    }

    const filtroVendedor = vendedorNum != null ? ` AND a.VENDEDOR = ${vendedorNum}` : '';
    const filtroRetirada =
      tipoRetirada === 'propria'
        ? ` AND (COALESCE(a.LJENTREGA, 0) = 0 OR a.LJENTREGA = a.CDFIL)`
        : tipoRetirada === 'outra'
          ? ` AND (COALESCE(a.LJENTREGA, 0) <> 0 AND a.LJENTREGA <> a.CDFIL)`
          : '';
    const schemas = ['estwin.', 'estwin950.', ''];
    const rowsTotal = [];

    const montarQuery = (schema) => `
      SELECT
        a.CDFIL,
        a.CPF,
        a.NRNFCE,
        a.SERIE,
        a.DATA,
        a.HORA,
        a.VENDEDOR,
        a.OPCAIXA,
        a.CDPRODU,
        a.QTD,
        a.DTENTREGA,
        a.HORAENTREGA,
        a.QTDENTREGUE,
        a.RESPONSAVEL,
        a.LOJATRANSF,
        a.FEZTRANSFERENCIA,
        a.DATAIMPRESSAO,
        a.SEPAROU,
        a.LJENTREGA,
        p.NOME AS NOMEPRO
      FROM ${schema}clienteretira a
      LEFT JOIN ${schema}sceprodu p ON a.CDPRODU = p.CDPRODU
      WHERE a.CDFIL = ${filialNum}
        AND CONCAT(MID(a.DATA,7,4),MID(a.DATA,4,2),MID(a.DATA,1,2)) >= '${dataIniYmd}'
        AND CONCAT(MID(a.DATA,7,4),MID(a.DATA,4,2),MID(a.DATA,1,2)) <= '${dataFimYmd}'
        ${filtroVendedor}
        ${filtroRetirada}
      ORDER BY CONCAT(MID(a.DATA,7,4),MID(a.DATA,4,2),MID(a.DATA,1,2)) DESC, a.HORA DESC, a.NRNFCE DESC
    `;

    for (const schema of schemas) {
      try {
        const rows = await queryService.execute(montarQuery(schema));
        if (Array.isArray(rows) && rows.length > 0) rowsTotal.push(...rows);
      } catch (_) {
        // Tabela/schema indisponível: continua.
      }
    }

    const chaves = new Set();
    const dedup = [];
    for (const row of rowsTotal) {
      const key = [
        row.CDFIL,
        row.NRNFCE,
        row.SERIE,
        row.CDPRODU,
        row.DATA,
        row.HORA,
        row.VENDEDOR
      ].join('|');
      if (!chaves.has(key)) {
        chaves.add(key);
        dedup.push(row);
      }
    }
    return dedup;
  }
};

/* ============================================================
   Avaliações de atendimento (pilotself.admcad1_venda) — SOMENTE LEITURA
   O totem de autoatendimento grava AVALIACAO de 1 a 4 (carinhas);
   0 = o cliente não avaliou. VENDEDOR = 1 marca venda espontânea.
   ============================================================ */
const AVALIACOES_CLIENTE = 2;
const AVALIACOES_VENDEDOR_ESPONTANEO = 1;

/**
 * FORMAPG em admcad1_venda é o enum do PRÓPRIO TOTEM — não é o CODIGO de
 * pilotself.admforma (a tabela de formas de pagamento da retaguarda). Usar
 * admforma faz um crédito virar "CHEQUE".
 *
 * Conferido cruzando as vendas com financall.admpagamentodetalhe (ORIGEM =
 * 'SELF') por loja + documento + data + valor: a correspondência foi de 100%
 * em ~4 mil vendas casadas, e o código 4 traz bandeira de crédito e parcelas.
 */
export const AVALIACOES_FORMAS_PAGAMENTO = {
  1: 'Compre+Rápido',
  2: 'PIX',
  3: 'Cartão de débito',
  4: 'Cartão de crédito',
  5: 'Indefinido'
};

export const rotuloFormaPagamento = (codigo) => {
  const numero = Number.parseInt(codigo, 10);
  if (!Number.isFinite(numero)) return '—';
  return AVALIACOES_FORMAS_PAGAMENTO[numero] || `Forma ${numero}`;
};

const validarDataYmd = (valor) => {
  const texto = String(valor || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto)) throw new Error('Informe um período válido.');
  return texto;
};

// A coluna é DATETIME: comparar com o dia seguinte (exclusivo) evita perder as
// vendas feitas depois das 00:00:00 do último dia do período.
const diaSeguinteYmd = (ymd) => {
  const [ano, mes, dia] = ymd.split('-').map(Number);
  const data = new Date(Date.UTC(ano, mes - 1, dia));
  data.setUTCDate(data.getUTCDate() + 1);
  return data.toISOString().slice(0, 10);
};

const numeroOuNulo = (valor) => {
  const texto = String(valor ?? '').trim();
  if (!texto) return null;
  const numero = Number.parseInt(texto, 10);
  return Number.isFinite(numero) ? numero : null;
};

const montarWhereAvaliacoes = ({
  dataIni,
  dataFim,
  loja = null,
  vendedor = null,
  tipoVenda = 'todos',
  notas = [],
  somenteAvaliadas = false
} = {}) => {
  const inicio = validarDataYmd(dataIni);
  const fimExclusivo = diaSeguinteYmd(validarDataYmd(dataFim));
  if (inicio > validarDataYmd(dataFim)) throw new Error('A data inicial não pode ser maior que a final.');

  const condicoes = [
    `CLIENTE = ${AVALIACOES_CLIENTE}`,
    `DATA_VENDA >= '${inicio} 00:00:00'`,
    `DATA_VENDA < '${fimExclusivo} 00:00:00'`
  ];

  const lojaNum = numeroOuNulo(loja);
  if (lojaNum != null) condicoes.push(`LOJA = ${lojaNum}`);

  const vendedorNum = numeroOuNulo(vendedor);
  if (vendedorNum != null) {
    condicoes.push(`VENDEDOR = ${vendedorNum}`);
  } else if (tipoVenda === 'espontanea') {
    condicoes.push(`VENDEDOR = ${AVALIACOES_VENDEDOR_ESPONTANEO}`);
  } else if (tipoVenda === 'vendedor') {
    condicoes.push(`VENDEDOR <> ${AVALIACOES_VENDEDOR_ESPONTANEO}`);
  }

  const notasValidas = [...new Set(
    (Array.isArray(notas) ? notas : [])
      .map((n) => Number.parseInt(n, 10))
      .filter((n) => n >= 1 && n <= 4)
  )];

  if (notasValidas.length > 0 && notasValidas.length < 4) {
    condicoes.push(`AVALIACAO IN (${notasValidas.sort().join(',')})`);
  } else if (notasValidas.length === 4 || somenteAvaliadas) {
    condicoes.push('AVALIACAO > 0');
  }

  return condicoes.join(' AND ');
};

// SUM()/COUNT() voltam do MySQL como string; o front precisa de número.
const AGREGADO_AVALIACOES_SQL = `
  COUNT(*) AS TOTAL_VENDAS,
  SUM(AVALIACAO > 0) AS AVALIADAS,
  SUM(AVALIACAO = 1) AS NOTA_1,
  SUM(AVALIACAO = 2) AS NOTA_2,
  SUM(AVALIACAO = 3) AS NOTA_3,
  SUM(AVALIACAO = 4) AS NOTA_4,
  SUM(CASE WHEN AVALIACAO > 0 THEN AVALIACAO ELSE 0 END) AS SOMA_NOTAS,
  SUM(CASE WHEN AVALIACAO > 0 THEN VALOR_TOTAL ELSE 0 END) AS VALOR_AVALIADO,
  SUM(VALOR_TOTAL) AS VALOR_TOTAL,
  SUM(VENDEDOR = ${AVALIACOES_VENDEDOR_ESPONTANEO}) AS ESPONTANEAS
`;

const CAMPOS_NUMERICOS_AGREGADO = [
  'TOTAL_VENDAS', 'AVALIADAS', 'NOTA_1', 'NOTA_2', 'NOTA_3', 'NOTA_4',
  'SOMA_NOTAS', 'VALOR_AVALIADO', 'VALOR_TOTAL', 'ESPONTANEAS'
];

const normalizarAgregado = (row = {}) => {
  const saida = { ...row };
  CAMPOS_NUMERICOS_AGREGADO.forEach((campo) => {
    saida[campo] = Number(row[campo] || 0);
  });
  saida.MEDIA = saida.AVALIADAS > 0 ? saida.SOMA_NOTAS / saida.AVALIADAS : 0;
  saida.SATISFACAO = saida.AVALIADAS > 0
    ? ((saida.NOTA_3 + saida.NOTA_4) / saida.AVALIADAS) * 100
    : 0;
  saida.TAXA_RESPOSTA = saida.TOTAL_VENDAS > 0
    ? (saida.AVALIADAS / saida.TOTAL_VENDAS) * 100
    : 0;
  return saida;
};

export const avaliacoesService = {
  // Cartões do topo + distribuição das 4 carinhas.
  resumo: async (filtros) => {
    const where = montarWhereAvaliacoes({ ...filtros, notas: [], somenteAvaliadas: false });
    const rows = await queryService.execute(`
      SELECT ${AGREGADO_AVALIACOES_SQL}
      FROM pilotself.admcad1_venda
      WHERE ${where}
    `);
    return normalizarAgregado(rows?.[0] || {});
  },

  // Série diária para o gráfico.
  serie: async (filtros) => {
    const where = montarWhereAvaliacoes({ ...filtros, notas: [], somenteAvaliadas: false });
    const rows = await queryService.execute(`
      SELECT DATE_FORMAT(DATA_VENDA, '%Y-%m-%d') AS DIA, ${AGREGADO_AVALIACOES_SQL}
      FROM pilotself.admcad1_venda
      WHERE ${where}
      GROUP BY DIA
      ORDER BY DIA
    `);
    return (rows || []).map((row) => ({ ...normalizarAgregado(row), DIA: row.DIA }));
  },

  // Ranking por filial.
  porLoja: async (filtros) => {
    const where = montarWhereAvaliacoes({ ...filtros, notas: [], somenteAvaliadas: false });
    const rows = await queryService.execute(`
      SELECT LOJA, ${AGREGADO_AVALIACOES_SQL}
      FROM pilotself.admcad1_venda
      WHERE ${where}
      GROUP BY LOJA
      ORDER BY LOJA
    `);
    return (rows || []).map((row) => ({ ...normalizarAgregado(row), LOJA: Number(row.LOJA) }));
  },

  // Ranking por matrícula. VENDEDOR = 1 nunca entra: é venda espontânea, não
  // pertence a ninguém. Os nomes vêm em consulta separada (scefun é grande e o
  // JOIN com a tabela de vendas custa caro no agrupamento).
  porVendedor: async (filtros, limite = 200) => {
    const where = montarWhereAvaliacoes({ ...filtros, notas: [], somenteAvaliadas: false });
    const limiteNum = Math.min(Math.max(Number.parseInt(limite, 10) || 200, 1), 500);
    const rows = await queryService.execute(`
      SELECT VENDEDOR, ${AGREGADO_AVALIACOES_SQL}
      FROM pilotself.admcad1_venda
      WHERE ${where} AND VENDEDOR <> ${AVALIACOES_VENDEDOR_ESPONTANEO}
      GROUP BY VENDEDOR
      HAVING AVALIADAS > 0
      ORDER BY AVALIADAS DESC
      LIMIT ${limiteNum}
    `);

    const agregados = (rows || []).map((row) => ({ ...normalizarAgregado(row), VENDEDOR: Number(row.VENDEDOR) }));
    const nomes = await avaliacoesService.nomesFuncionarios(agregados.map((r) => r.VENDEDOR));
    return agregados.map((row) => ({ ...row, NOME: nomes[row.VENDEDOR] || null }));
  },

  // Lista analítica (é aqui que o filtro de carinhas selecionadas vale).
  listar: async (filtros, limite = 400) => {
    const where = montarWhereAvaliacoes(filtros);
    const limiteNum = Math.min(Math.max(Number.parseInt(limite, 10) || 400, 1), 2000);
    const rows = await queryService.execute(`
      SELECT
        ID,
        LOJA,
        CAIXA,
        NFCE,
        SERIE,
        VALOR_TOTAL,
        VALOR_DESCONTO,
        TOTAL_PRODUTOS,
        FORMAPG,
        AVALIACAO,
        VENDEDOR,
        CPFNANOTA,
        NFCE_CHAVE,
        PREVENDA,
        DATE_FORMAT(DATA_VENDA, '%Y-%m-%d') AS DIA,
        DATE_FORMAT(DATA_VENDA, '%d/%m/%Y %H:%i') AS DATA_HORA,
        DATE_FORMAT(DATA_VENDA, '%H:%i') AS HORA
      FROM pilotself.admcad1_venda
      WHERE ${where}
      ORDER BY DATA_VENDA DESC
      LIMIT ${limiteNum}
    `);

    const vendas = (rows || []).map((row) => ({
      ...row,
      ID: Number(row.ID),
      LOJA: Number(row.LOJA),
      AVALIACAO: Number(row.AVALIACAO || 0),
      VENDEDOR: Number(row.VENDEDOR || 0),
      VALOR_TOTAL: Number(row.VALOR_TOTAL || 0),
      VALOR_DESCONTO: Number(row.VALOR_DESCONTO || 0),
      TOTAL_PRODUTOS: Number(row.TOTAL_PRODUTOS || 0)
    }));

    const nomes = await avaliacoesService.nomesFuncionarios(
      vendas.map((v) => v.VENDEDOR).filter((v) => v !== AVALIACOES_VENDEDOR_ESPONTANEO)
    );
    return vendas.map((venda) => ({ ...venda, VENDEDOR_NOME: nomes[venda.VENDEDOR] || null }));
  },

  // Itens do cupom avaliado.
  itensVenda: async (vendaId) => {
    const id = numeroOuNulo(vendaId);
    if (id == null) return [];

    const itens = await queryService.execute(`
      SELECT PRODUTO, QTD, VALOR_BRUTO, VALOR_DESCONTO, VALOR_LIQUIDO, SUBTOTAL, ORDEM
      FROM pilotself.admcad1_venda_produto
      WHERE VENDA = ${id}
      ORDER BY ORDEM
    `);

    const linhas = (itens || []).map((item) => ({
      ...item,
      PRODUTO: Number(item.PRODUTO),
      QTD: Number(item.QTD || 0),
      VALOR_DESCONTO: Number(item.VALOR_DESCONTO || 0),
      SUBTOTAL: Number(item.SUBTOTAL || 0)
    }));
    if (linhas.length === 0) return [];

    const codigos = [...new Set(linhas.map((l) => l.PRODUTO))].filter(Number.isFinite);
    let nomes = {};
    if (codigos.length > 0) {
      try {
        const produtos = await queryService.execute(`
          SELECT CDPRODU, NOME
          FROM estwin.sceprodu
          WHERE CDPRODU IN (${codigos.join(',')})
        `);
        nomes = (produtos || []).reduce((acc, p) => {
          acc[Number(p.CDPRODU)] = p.NOME;
          return acc;
        }, {});
      } catch (_) {
        // Sem descrição o item ainda aparece pelo código.
      }
    }

    return linhas.map((linha) => ({ ...linha, NOME: nomes[linha.PRODUTO] || null }));
  },

  nomesFuncionarios: async (matriculas) => {
    const codigos = [...new Set(
      (matriculas || []).map((m) => Number.parseInt(m, 10)).filter((m) => Number.isFinite(m) && m > 0)
    )];
    if (codigos.length === 0) return {};

    try {
      const rows = await queryService.execute(`
        SELECT CDFUN, NOME
        FROM estwin.scefun
        WHERE CDFUN IN (${codigos.join(',')})
      `);
      return (rows || []).reduce((acc, row) => {
        acc[Number(row.CDFUN)] = row.NOME;
        return acc;
      }, {});
    } catch (_) {
      return {};
    }
  }
};

export const bannersService = {
  async listar({ dataIni, dataFim, page = 1, perPage = 50, filtro = '' } = {}) {
    const response = await apiProxy.get('/callfarma/banner-prevendas', {
      params: {
        dataIni,
        dataFim,
        filtro,
        page,
        per_page: perPage
      }
    });
    if (response.data?.success) {
      return response.data.data;
    }
    return null;
  }
};

const solicitarProtocoloOnline = async ({
  chaveNfe,
  chavesNfe = [],
  documentos = [],
  pedido,
  origemCdfil,
  preview = false,
  confirmacaoDestino = null
}) => {
  const chave = String(chaveNfe || '').replace(/\s+/g, '');
  const numeroPedido = String(pedido || '').trim();
  const documentosEntrada = [];

  if (Array.isArray(documentos)) documentosEntrada.push(...documentos);
  if (Array.isArray(chavesNfe)) {
    documentosEntrada.push(...chavesNfe.map((documento) => ({ documento_tipo: 1, documento })));
  }
  if (chave) documentosEntrada.push({ documento_tipo: 1, documento: chave });

  const documentosNormalizados = [];
  const documentosUnicos = new Set();
  for (let index = 0; index < documentosEntrada.length; index += 1) {
    const entrada = documentosEntrada[index];
    const item = entrada && typeof entrada === 'object'
      ? entrada
      : { documento_tipo: 1, documento: entrada };
    const documentoTipo = Number.parseInt(item.documento_tipo ?? item.tipo ?? 1, 10);
    const documento = String(item.documento || '').replace(/\s+/g, '');

    if (!documentoTipo || documentoTipo < 1) {
      throw new Error(`Tipo inválido no documento ${index + 1}`);
    }
    if (documentoTipo === 1 && !/^\d{44}$/.test(documento)) {
      throw new Error(`A chave da NF-e ${index + 1} deve conter 44 dígitos`);
    }
    if (documentoTipo !== 1 && !documento) {
      throw new Error(`Documento ${index + 1} não informado`);
    }

    const identificador = `${documentoTipo}|${documento}`;
    if (!documentosUnicos.has(identificador)) {
      documentosUnicos.add(identificador);
      documentosNormalizados.push({ documento_tipo: documentoTipo, documento });
    }
  }

  if (!documentosNormalizados.length && !numeroPedido) {
    throw new Error('Informe ao menos uma chave de NF-e ou o número do pedido');
  }
  if (documentosNormalizados.length > 100) {
    throw new Error('Limite de 100 documentos por protocolo');
  }

  const payload = {};
  if (documentosNormalizados.length) payload.documentos = documentosNormalizados;
  if (numeroPedido) payload.pedido = numeroPedido;
  if (origemCdfil) payload.origem_cdfil = Number(origemCdfil);
  if (preview) payload.preview = true;
  if (confirmacaoDestino) {
    payload.confirmacao_destino = {
      destinatario_cdfun: confirmacaoDestino.destinatario_cdfun,
      destinatario_cdfil: confirmacaoDestino.destinatario_cdfil,
    };
  }

  try {
    const response = await apiProxy.post('/proxy/protocolo-online', payload);
    if (response.data?.success) return response.data;
    throw new Error(response.data?.error || 'Erro ao processar protocolo');
  } catch (error) {
    const mensagem = error?.response?.data?.error || error?.message || 'Erro ao processar protocolo';
    const detalhes = error?.response?.data?.chaves;
    if (Array.isArray(detalhes) && detalhes.length) {
      const chaves = detalhes.map((item) =>
        typeof item === 'string' ? item : (item?.chave || JSON.stringify(item))
      );
      throw new Error(`${mensagem} ${chaves.join(', ')}`);
    }
    throw new Error(mensagem);
  }
};

export const protocoloOnlineService = {
  async listarPedidosEmitidos({ dias = 1, limite = 100, origemCdfil } = {}) {
    try {
      const response = await apiProxy.get('/proxy/protocolo-online/pedidos-emitidos', {
        params: { dias, limite, origem_cdfil: origemCdfil }
      });
      if (response.data?.success) return response.data;
      throw new Error(response.data?.error || 'Erro ao carregar pedidos emitidos');
    } catch (error) {
      throw new Error(
        error?.response?.data?.error || error?.message || 'Erro ao carregar pedidos emitidos'
      );
    }
  },

  async preview({ chaveNfe, chavesNfe, documentos, pedido, origemCdfil }) {
    return solicitarProtocoloOnline({
      chaveNfe,
      chavesNfe,
      documentos,
      pedido,
      origemCdfil,
      preview: true
    });
  },

  async create({ chaveNfe, chavesNfe, documentos, pedido, origemCdfil, confirmacaoDestino }) {
    return solicitarProtocoloOnline({
      chaveNfe,
      chavesNfe,
      documentos,
      pedido,
      origemCdfil,
      confirmacaoDestino
    });
  },

  async finish({ protocoloId, cdfil, cdfun }) {
    const id = String(protocoloId || '').trim();
    if (!id) throw new Error('Informe o número do protocolo');
    if (!cdfil) throw new Error('CDFIL obrigatório');
    if (!cdfun) throw new Error('CDFUN (matrícula) obrigatório');

    const response = await apiProxy.put(`/proxy/protocolo-online/${encodeURIComponent(id)}/finish`, {
      cdfil: String(cdfil),
      cdfun: String(cdfun)
    });

    if (response.data?.success) return response.data;
    throw new Error(response.data?.error || 'Erro ao finalizar protocolo');
  }
};

export const posicaoEstoqueService = {
  async buscarSugestoes(termo, limite = 8) {
    const response = await apiProxy.post('/proxy/posicao-estoques/busca', { termo, limite });
    if (response.data?.success) return response.data.data || [];
    return [];
  },

  async consultarCompleto({ codigo, filialBusca = 22, cdfil = 'TODAS', anos = [] }) {
    const response = await apiProxy.post('/proxy/posicao-estoques/consulta', {
      codigo,
      filialBusca,
      cdfil,
      anos,
    });
    if (response.data?.success) return response.data.data || {};
    throw new Error(response.data?.error || 'Erro ao consultar posição de estoques');
  },

  async buscarProduto(codigo, filial = 22) {
    return produtoService.buscarPreco(codigo, filial);
  },

  async buscarPosicao({ codigo, cdfil = 'TODAS' }) {
    const codigoNum = parseInt(String(codigo || '').trim(), 10);
    if (!Number.isFinite(codigoNum)) throw new Error('Código do produto inválido');

    const filtroFilial = cdfil === 'TODAS' ? '' : ` AND a.CDFIL = ${parseInt(cdfil, 10)}`;
    const query = `
      SELECT
        a.CDPRODU,
        a.CDFIL,
        b.ABREV,
        a.ABC,
        a.DEMDIA,
        a.DEMMEN,
        a.ESTMAX,
        a.ESTMIN,
        a.ESTOQ,
        a.QTDPED,
        a.MINPRO,
        a.OBJETIVOMES,
        a.MININI,
        a.PRECOPOR,
        a.PRECOMAX,
        a.PRECOKIT,
        a.MINFIM,
        a.ULTSAI,
        a.PREPRO,
        a.INIPRO,
        a.FINPRO,
        a.QTDTRN,
        a.ESTAEMRUPTURA,
        a.FIMDARUPTURA,
        a.CDPRODUMIGRADO,
        a.ESTOQUE_ROBO,
        a.QTDENT
      FROM estwin.sceestoq a
      JOIN estwin.scefilial b ON a.CDFIL = b.CDFIL
      WHERE a.CDEMP = '1'
        AND a.CDPRODU = '${codigoNum}'
        ${filtroFilial}
      ORDER BY a.CDFIL
    `;
    return queryService.execute(query);
  },

  async buscarHistorico({ cdfil, codigo, ano }) {
    const codigoNum = parseInt(String(codigo || '').trim(), 10);
    const filialNum = parseInt(String(cdfil || '').trim(), 10);
    const anoNum = parseInt(String(ano || '').trim(), 10);
    if (!Number.isFinite(codigoNum) || !Number.isFinite(filialNum) || !Number.isFinite(anoNum)) {
      throw new Error('Parâmetros inválidos para histórico');
    }

    const query = `
      SELECT *
      FROM estwin.scehist
      WHERE CDEMP = '1'
        AND CDFIL = '${filialNum}'
        AND CDPRODU = '${codigoNum}'
        AND ANO = '${anoNum}'
      LIMIT 1
    `;
    const rows = await queryService.execute(query);
    return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
  },

  async buscarDetalhesTransito({ codigo, filial }) {
    const codigoNum = parseInt(String(codigo || '').trim(), 10);
    const filialNum = parseInt(String(filial || '').trim(), 10);
    if (!Number.isFinite(codigoNum) || !Number.isFinite(filialNum)) {
      throw new Error('Produto ou filial inválidos para detalhes.');
    }

    const run = async (query) => {
      try {
        const rows = await queryService.execute(query);
        return Array.isArray(rows) ? rows : [];
      } catch (error) {
        console.error('[posicao-estoques] Erro ao buscar detalhes:', error);
        return [];
      }
    };

    const queryNotas = `
      SELECT
        a.NRNOTA,
        a.QTD,
        a.DATA,
        a.VLRUNI,
        b.ABREV,
        a.VLRSUBS,
        a.CDFORNE,
        a.NRSERIE,
        (
          SELECT i.CHAVENFE
          FROM sceitensnfent i
          WHERE i.CDEMP = a.CDEMP
            AND i.CDFIL = a.CDFIL
            AND i.CDFORNE = a.CDFORNE
            AND i.NRNOTA = a.NRNOTA
            AND i.SERIE = a.NRSERIE
          LIMIT 1
        ) AS CHAVENFE
      FROM sceent a, sceforne b
      WHERE a.CDEMP = '1'
        AND a.CDFIL = ${filialNum}
        AND a.CDPRODU = '${codigoNum}'
        AND a.CDFORNE = b.CDFORNE
      ORDER BY a.DATA DESC, a.NRNOTA DESC
      LIMIT 6
    `;

    const queryPedidos = `
      SELECT
        a.NRPEDIDO,
        a.QTD,
        a.DATA,
        a.VLRPED,
        b.ABREV,
        b.CDCOMPRADOR,
        c.PEDELETRONICO,
        c.OPER,
        a.PEDELETRONICO AS PEDELE
      FROM scepedf a, sceforne b, scepedfc c
      WHERE a.CDEMP = '1'
        AND a.CDFIL = ${filialNum}
        AND a.CDPRODU = '${codigoNum}'
        AND a.CDFORNE = b.CDFORNE
        AND a.NRPEDIDO = c.NRPEDIDO
      ORDER BY a.DATA DESC
      LIMIT 6
    `;

    const queryConferencias = `
      SELECT
        a.NRNOTA,
        a.QTD,
        a.DATACONF,
        b.ABREV,
        a.ENDERECO,
        a.CDFORNE,
        a.NRSERIE,
        a.QTDEMB,
        (
          SELECT i.CHAVENFE
          FROM sceitensnfent i
          WHERE i.CDEMP = a.CDEMP
            AND i.CDFIL = a.CDFIL
            AND i.CDFORNE = a.CDFORNE
            AND i.NRNOTA = a.NRNOTA
            AND i.SERIE = a.NRSERIE
          LIMIT 1
        ) AS CHAVENFE
      FROM sceentconf a, sceforne b
      WHERE a.CDEMP = '1'
        AND a.CDFIL = ${filialNum}
        AND a.CDPRODU = '${codigoNum}'
        AND a.CDFORNE = b.CDFORNE
      ORDER BY a.DATACONF DESC
      LIMIT 6
    `;

    const queryPedidosDeposito = `
      SELECT *
      FROM (
        (
          SELECT
            a.NRPEDIDO,
            b.QTD,
            a.DATA,
            b.QTDCONF,
            a.CDFIL,
            a.FGSTATUS
          FROM estwin.scepedc a, estwin.scepedi b
          WHERE b.CDPRODU = '${codigoNum}'
            AND b.NRPEDIDO = a.NRPEDIDO
            AND a.NRPEDIDO = b.NRPEDIDO
            AND a.CDCLI = ${filialNum}
          LIMIT 6
        )
        UNION
        (
          SELECT
            a.NRPEDIDO,
            b.QTD,
            a.DATA,
            b.QTDCONF,
            a.CDFIL,
            a.FGSTATUS
          FROM estwin950.scepedc a, estwin950.scepedi b
          WHERE b.CDPRODU = '${codigoNum}'
            AND b.NRPEDIDO = a.NRPEDIDO
            AND a.NRPEDIDO = b.NRPEDIDO
            AND a.CDCLI = ${filialNum}
          LIMIT 6
        )
      ) t1
      ORDER BY DATA DESC
    `;

    const [notas, pedidos, conferencias, pedidosDeposito] = await Promise.all([
      run(queryNotas),
      run(queryPedidos),
      run(queryConferencias),
      run(queryPedidosDeposito)
    ]);

    return {
      notas,
      pedidos,
      conferencias,
      pedidosDeposito,
      chaveNfe: notas.find((item) => item.CHAVENFE)?.CHAVENFE || conferencias.find((item) => item.CHAVENFE)?.CHAVENFE || ''
    };
  }
};

// Serviço de busca de produtos
export const produtoService = {
  buscarPorNome: async (nome, filial) => {
    const query = `
      SELECT
        p.CDPRODU,
        p.NOME,
        p.PBM,
        COALESCE(e.ESTOQ, 0) as ESTOQUE,
        COALESCE(e.ENDER, 'Não informado') as ENDERECO,
        COALESCE(e.PRECOPOR, e.PRECOMAX, 0) as PRECO,
        COALESCE(e.PRECOMAX, 0) as PRECO_SEM_DESCONTO
      FROM sceprodu p
      LEFT JOIN sceestoq e ON e.CDPRODU = p.CDPRODU AND e.CDFIL = ${filial}
      WHERE p.NOME LIKE '%${nome}%'
        AND p.FGATIVO = 'S'
      ORDER BY COALESCE(e.ESTOQ, 0) DESC, p.NOME
      LIMIT 50
    `;
    return queryService.execute(query);
  },

  buscarPreco: async (codigo, filial) => {
    const response = await apiProxy.post('/proxy/consulta-preco/buscar-preco', {
      codigo,
      filial
    });

    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }

    throw new Error(response.data?.message || 'Erro ao consultar preço');
  },

  buscarEstoque: async (codigo) => {
    const response = await apiProxy.post('/proxy/consulta-preco/estoque', { codigo });
    if (!response.data?.success || !Array.isArray(response.data?.data?.estoque)) {
      return [];
    }
    return (response.data.data.estoque || []).map((item) => ({
      ...item,
      ENDER: item.ENDER || item.ENDERECO || ''
    }));
  },

  buscarEstoqueFilialPorCodigoLido: async (codigo, filial) => {
    const response = await apiProxy.post('/proxy/consulta-preco/estoque', { codigo });
    if (!response.data?.success || !response.data?.data?.estoque) {
      return null;
    }
    const filialStr = String(filial);
    return (response.data.data.estoque || []).find((item) => String(item.CDFIL) === filialStr) || null;
  },

  buscarHistorico: async (codigo, filial, dataIni, dataFim) => {
    const query = `
      SELECT
        k.DATA,
        k.HORA,
        k.CDFUN,
        k.CDOPE,
        k.TIPO,
        k.CDFORNE,
        k.QTD,
        k.DOC,
        k.ESTOQU,
        k.LOTE,
        k.nRECF,
        k.nrCPF
      FROM scekarde k
      WHERE k.CDFIL = ${filial}
        AND k.CDPRODU IN (
          SELECT CDPRODU
          FROM sceprodu
          WHERE CDPRODU = '${codigo}'
             OR BARRA = '${codigo}'
             OR BAR1  = '${codigo}'
             OR BAR2  = '${codigo}'
             OR BAR3  = '${codigo}'
             OR BAR4  = '${codigo}'
        )
        AND k.DATA >= '${dataIni}'
        AND k.DATA <= '${dataFim}'
      ORDER BY k.DATA ASC, k.HORA ASC
      LIMIT 100
    `;
    return queryService.execute(query);
  }
};

// Serviço de movimentação manual de estoque (kardex + saldo)
export const movimentacaoEstoqueService = {
  // Saldo atual do produto na filial (usa o SELECT-only /api/query)
  buscarSaldo: async (codigo, filial) => {
    const cod = String(codigo || '').trim().replace(/'/g, '');
    const fil = parseInt(filial, 10) || 0;
    const query = `
      SELECT p.CDPRODU, p.NOME, COALESCE(e.ESTOQ, 0) AS ESTOQ,
             COALESCE(e.ENDER, '') AS ENDER
      FROM sceprodu p
      LEFT JOIN sceestoq e ON e.CDPRODU = p.CDPRODU AND e.CDFIL = ${fil}
      WHERE p.CDPRODU = '${cod}' OR p.BARRA = '${cod}'
         OR p.BAR1 = '${cod}' OR p.BAR2 = '${cod}'
         OR p.BAR3 = '${cod}' OR p.BAR4 = '${cod}'
      LIMIT 1
    `;
    const rows = await queryService.execute(query);
    return rows && rows.length ? rows[0] : null;
  },

  // Grava o movimento via endpoint dedicado (escrita ocorre no proxy, em transação)
  registrar: async ({ cdprodu, cdfil, operacao, qtd, lote = '', doc = '',
                      cdfun = 0, cdope = 0, valor = 0, ajusteParaSaldo = null }) => {
    const response = await apiProxy.post('/proxy/estoque/movimentacao', {
      cdprodu, cdfil, operacao, qtd, lote, doc, cdfun, cdope, valor, ajusteParaSaldo
    });
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Falha ao registrar movimentação.');
    }
    return response.data; // { success, saldoAnterior, saldoNovo, movimento }
  },

  // Cria uma transferência de mercadoria (origem -> destino), gravada como
  // pendente do Depto de Compras (TIPO='C') sob um único DOC no endpoint dedicado.
  // doc opcional: se informado, lança os itens num DOC já existente (pendente).
  criarTransferencia: async ({ cdfil, cdfildest, itens, cdope = 0, cdfun = 0, cdemp = 1, doc = null }) => {
    const response = await apiProxy.post('/proxy/estoque/transferencia', {
      cdfil, cdfildest, itens, cdope, cdfun, cdemp, doc
    });
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Falha ao criar transferência.');
    }
    return response.data; // { success, doc, itens, detalhes }
  },

  // Lista a "Movimentação Pendente" da filial (1 linha por ITEM), mesmas
  // colunas da versão antiga: FIL, COD, PRODUTO, QTD, DATA, REQUIS., DESTINO, NR DOC.
  // (espelha o SELECT do estwin: a.CDFIL, a.CDPRODU, b.NOME, a.DATA, a.QTD,
  //  a.CDFILDEST, a.DOC, a.CDOPE  —  REQUIS = operador/Requisitante = CDOPE,
  //  cujo nome vem da tabela gap002)
  // tipo: 'C' (Depto Compras, padrão) | 'R' (remanejamento) | 'P' (pré-vencidos)
  //     | 'TODOS' (sem filtro). Só linhas 'C' são canceláveis/editáveis pela tela —
  //     as demais vêm para visualização (ver taxonomia em rotas-estoque-cadastro.js).
  listarPendentesOrigem: async (cdfil, tipo = 'C') => {
    const fil = parseInt(cdfil, 10) || 0;
    const filtroTipo = ['C', 'R', 'P'].includes(tipo) ? `AND a.TIPO = '${tipo}'` : '';
    const query = `
      SELECT a.CDFIL AS FIL,
             a.CDPRODU AS COD,
             b.NOME AS PRODUTO,
             a.QTD,
             DATE_FORMAT(a.DATA, '%d/%m/%Y') AS DATA,
             a.CDOPE AS REQUIS_COD,
             COALESCE(NULLIF(TRIM(g.NOME), ''), a.CDOPE) AS REQUIS,
             a.CDFILDEST AS DESTINO,
             COALESCE(fd.ABREV, a.CDFILDEST) AS DESTINO_NOME,
             a.DOC AS NRDOC,
             a.TIPO
      FROM estwin.scemov_transferencia a
      JOIN estwin.sceprodu b ON b.CDPRODU = a.CDPRODU
      LEFT JOIN estwin.gap002 g ON g.CDOPE = a.CDOPE
      LEFT JOIN estwin.scefilial fd ON fd.CDFIL = a.CDFILDEST
      WHERE a.CDFIL = ${fil}
        ${filtroTipo}
        AND a.MARCADOPARAAPAGAR <> 'S'
      ORDER BY a.DOC DESC, b.NOME
      LIMIT 500
    `;
    return queryService.execute(query);
  },

  // Cancela uma transferência pendente (apaga as linhas + estorna QTDTRN).
  // Sem cdprodu => cancela o DOC inteiro. Com cdprodu/cdfildest => só o item.
  // tipo: 'C' (Depto Compras) ou 'R' (remanejamento). 'P' o backend recusa.
  cancelarTransferencia: async ({ cdfil, doc, cdprodu = null, cdfildest = null, tipo = 'C' }) => {
    const response = await apiProxy.post('/proxy/estoque/transferencia/cancelar', {
      cdfil, doc, cdprodu, cdfildest, tipo
    });
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Falha ao cancelar transferência.');
    }
    return response.data; // { success, doc, cdprodu, itensCancelados }
  }
};

// Serviço de alteração de cadastro (cliente / funcionário)
export const cadastroService = {
  // ---------- CLIENTE (televendas.pdvcliente) ----------
  buscarCliente: async (termo) => {
    const t = String(termo || '').trim().replace(/'/g, '');
    if (!t) return [];
    const soDigitos = t.replace(/\D/g, '');
    let where;
    if (/^\d+$/.test(t)) {
      // número: tenta por CODIGO ou CPF
      where = `CODIGO = ${t} OR CPF_LIMPO = ${soDigitos.replace(/^0+/, '') || 0}`;
    } else {
      where = `NOME LIKE '%${t.toUpperCase()}%'`;
    }
    const query = `
      SELECT CODIGO, NOME, CPF, IDENTIDADE, NASCIMENTO, SEXO, FONE, CELULAR,
             C20_FONECO, EMAIL, CEP, ENDE, NRENDE, COMPL, BAIR, CIDA, ESTA,
             PONTOREF, NOMEMAE, RECEBEOFERTA,
             SNGPC_TIPODOC, SNGPC_NUMDOC, SNGPC_ORGAO, SNGPC_UF, NRCRM
      FROM televendas.pdvcliente
      WHERE ${where}
      ORDER BY NOME
      LIMIT 50
    `;
    return queryService.execute(query);
  },

  salvarCliente: async (codigo, campos) => {
    const response = await apiProxy.post('/proxy/cadastro/cliente', { codigo, campos });
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Falha ao salvar cliente.');
    }
    return response.data;
  },

  // ---------- FUNCIONÁRIO (estwin.scefun) ----------
  buscarFuncionario: async (termo) => {
    const t = String(termo || '').trim().replace(/'/g, '');
    if (!t) return [];
    const where = /^\d+$/.test(t)
      ? `CDFUN = ${t}`
      : `NOME LIKE '%${t.toUpperCase()}%'`;
    const query = `
      SELECT CDFUN, CDEMP, CDFIL, NRFUNC, NOME, RG, CPF, PIS, FGSITUAC,
             DATE_FORMAT(DTNASCIMENTO,'%Y-%m-%d') AS DTNASCIMENTO,
             DATE_FORMAT(ADMISSAO,'%Y-%m-%d') AS ADMISSAO,
             DATE_FORMAT(DATADEMISSAO,'%Y-%m-%d') AS DATADEMISSAO,
             MOTIVODEMISSAO, SETOR, NIVEL, ENDERECO, TELEFONE, CELULAR, EMAIL,
             LIMITE, HORAENT, HORASAI, HORAINTINI, HORAINTFIM, OBS
      FROM estwin.scefun
      WHERE ${where}
      ORDER BY NOME
      LIMIT 50
    `;
    return queryService.execute(query);
  },

  salvarFuncionario: async (cdfun, campos, cdemp = 1) => {
    const response = await apiProxy.post('/proxy/cadastro/funcionario', { cdfun, cdemp, campos });
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Falha ao salvar funcionário.');
    }
    return response.data;
  },

  // --- Produto ---
  // SELECT * (como o legado) — bulletproof contra coluna inexistente; o form
  // só lê os campos que conhece.
  buscarProduto: async (termo) => {
    const t = String(termo || '').trim().replace(/'/g, '');
    if (!t) return [];
    const somenteNum = /^\d+$/.test(t);
    const where = somenteNum
      ? `CDPRODU = '${t}' OR BARRA = '${t}' OR BAR1 = '${t}' OR BAR2 = '${t}' OR BAR3 = '${t}' OR BAR4 = '${t}' OR RMS = '${t}'`
      : `NOME LIKE '%${t}%'`;
    const query = `SELECT * FROM estwin.sceprodu WHERE ${where} ORDER BY NOME LIMIT 50`;
    return queryService.execute(query);
  },

  salvarProduto: async (cdprodu, campos) => {
    const response = await apiProxy.post('/proxy/cadastro/produto', { cdprodu, campos });
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Falha ao salvar produto.');
    }
    return response.data;
  },

  // --- Foto do produto (BLOB em sceproducompl.FOTO) ---
  // Busca a foto atual como objectURL (ou null se 404). Usa Bearer via interceptor.
  buscarFotoProduto: async (cdprodu) => {
    try {
      const resp = await apiProxy.get('/proxy/cadastro/produto/foto', {
        params: { cdprodu }, responseType: 'blob',
      });
      return URL.createObjectURL(resp.data);
    } catch (e) {
      return null; // sem foto
    }
  },

  salvarFotoProduto: async (cdprodu, file, fotosite = null) => {
    // lê o arquivo como dataURL base64
    const foto = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => reject(new Error('Falha ao ler o arquivo.'));
      fr.readAsDataURL(file);
    });
    const resp = await apiProxy.post('/proxy/cadastro/produto/foto', {
      cdprodu, foto, fotosite: fotosite == null ? undefined : (fotosite ? 'S' : 'N'),
    });
    if (!resp.data?.success) throw new Error(resp.data?.error || 'Falha ao gravar a foto.');
    return resp.data;
  },

  atualizarSiteProduto: async (cdprodu) => {
    const resp = await apiProxy.post('/proxy/cadastro/produto/atualizar-site', { cdprodu });
    if (!resp.data?.success) throw new Error(resp.data?.error || 'Falha ao atualizar o site.');
    return resp.data;
  }
};

// Reimpressão de cupom (NFC-e) - só leitura (SELECT).
// O nº do cupom repete por dia/loja: a chave operacional é data + filial + cupom.
export const reimpressaoCupomService = {
  buscarCupom: async (cupom, cdfil, dataYmd) => {
    const c = parseInt(cupom, 10);
    const fil = parseInt(cdfil, 10) || 0;
    const ymd = String(dataYmd || '').trim(); // 'YYYY-MM-DD'
    if (!c || !fil || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      throw new Error('Informe data, filial e número do cupom.');
    }
    const [ano, mes, dia] = ymd.split('-');
    const dataBr = `${dia}/${mes}/${ano}`;
    const buscarDadosFilial = async () => {
      try {
        const response = await apiProxy.get(`/proxy/lojas/${fil}/dados`);
        if (response.data?.success && response.data?.data) {
          return [response.data.data];
        }
      } catch (error) {
        console.warn('Falha ao buscar dados da loja pelo proxy, usando fallback SQL:', error?.message || error);
      }

      return queryService.execute(
        `SELECT CDFIL, NOME, ENDER, NREND, CIDADE, CEP, CGC
         FROM scefilial
         WHERE CDFIL = ${fil}
         LIMIT 1`
      );
    };

    const [cupomRows, itensRows, pagamentosRows, filialRows] = await Promise.all([
      queryService.execute(
        `SELECT
           k.DOC AS CUPOM,
           k.CDFIL AS LOJA,
           k.DATA,
           MIN(k.HORA) AS HORA,
           MAX(k.CDFUN) AS VENDEDOR,
           SUM(k.VALOR) AS VLRTOTAL,
           SUM(k.QTD) AS QTDITENS,
           MAX(k.nrCPF) AS CPF_CLIENTE
         FROM scekarde k
         WHERE k.TIPO IN ('VE', 'DV')
           AND k.DOC = ${c}
           AND k.CDFIL = ${fil}
           AND k.DATA = '${ymd}'
         GROUP BY k.DOC, k.CDFIL, k.DATA
         LIMIT 1`),
      queryService.execute(
        `SELECT
           k.DOC AS CUPOM,
           k.CDPRODU,
           p.NOME,
           p.BARRA,
           p.BAR1,
           p.BAR2,
           SUM(k.QTD) AS QTD,
           CASE WHEN SUM(k.QTD) = 0 THEN 0 ELSE SUM(k.VALOR) / SUM(k.QTD) END AS VLRUNI,
           0 AS DESCONTO,
           SUM(k.VALOR) AS VLRTOTAL
         FROM scekarde k
         LEFT JOIN sceprodu p ON p.CDPRODU = k.CDPRODU
         WHERE k.TIPO IN ('VE', 'DV')
           AND k.DOC = ${c}
           AND k.CDFIL = ${fil}
           AND k.DATA = '${ymd}'
         GROUP BY k.DOC, k.CDPRODU, p.NOME, p.BARRA, p.BAR1, p.BAR2
         ORDER BY p.NOME, k.CDPRODU`),
      queryService.execute(
        `SELECT DISTINCT
           DOCUMENTO,
           LOJA,
           DATA,
           FORMAPG,
           BANDEIRA,
           PARCELAS,
           VALOR,
           NSU,
           TID,
           PAYMENTID,
           STATUS_SITE,
           NRAUTORIZACAO
         FROM financall.admpagamentodetalhe
         WHERE DOCUMENTO = '${String(c).replace(/'/g, "''")}'
           AND LOJA = ${fil}
           AND DATA IN ('${ymd}', '${dataBr}')
         ORDER BY LOJA, DATA, DOCUMENTO, REGID`),
      buscarDadosFilial(),
    ]);

    const cab = (cupomRows && cupomRows[0]) || null;
    if (!cab) throw new Error('Cupom não encontrado para essa data + filial + número.');
    const totalCupom = Number(cab.VLRTOTAL) || 0;
    const totalSql = totalCupom.toFixed(2);
    const horaCupomRef = String(cab.HORA || '00:00:00').slice(0, 8);
    const dataHoraCupomSql = `${ymd} ${horaCupomRef}`;
    const nfSelect = `
      SELECT
        NRNFCE AS NOTA,
        SERIENFCE AS SERIE,
        CHAVE,
        PROTOCOLO,
        LINK,
        DATE_FORMAT(DATA, '%d/%m/%Y') AS DTEMIS,
        CPF,
        CPFNANOTA,
        VALOR,
        DOCUMENTO,
        DOCUMENTO_ORIGEM
      FROM scenfce_emitidas
    `;
    const nfOrder = `
      ORDER BY
        CASE
          WHEN DOCUMENTO = ${c} THEN 0
          WHEN DOCUMENTO_ORIGEM = ${c} THEN 1
          WHEN NRNFCE = ${c} THEN 2
          ELSE 3
        END,
        ABS(COALESCE(VALOR, 0) - ${totalSql}) ASC,
        ABS(TIMESTAMPDIFF(SECOND, DATA, '${dataHoraCupomSql}')) ASC,
        DATA DESC,
        id DESC
      LIMIT 1
    `;
    const nfQueries = [
      `${nfSelect}
       WHERE CDFIL = ${fil}
         AND DATE(DATA) = '${ymd}'
         AND (
           DOCUMENTO = ${c}
           OR DOCUMENTO_ORIGEM = ${c}
           OR NRNFCE = ${c}
         )
       ${nfOrder}`,
      `${nfSelect}
       WHERE CDFIL = ${fil}
         AND DATE(DATA) = '${ymd}'
         AND ABS(COALESCE(VALOR, 0) - ${totalSql}) <= 0.10
       ${nfOrder}`,
      `${nfSelect}
       WHERE CDFIL = ${fil}
         AND DATE(DATA) BETWEEN DATE_SUB('${ymd}', INTERVAL 1 DAY) AND DATE_ADD('${ymd}', INTERVAL 1 DAY)
         AND (
           DOCUMENTO = ${c}
           OR DOCUMENTO_ORIGEM = ${c}
           OR NRNFCE = ${c}
           OR ABS(COALESCE(VALOR, 0) - ${totalSql}) <= 0.10
         )
       ${nfOrder}`
    ];

    let nfRows = [];
    for (const query of nfQueries) {
      nfRows = await queryService.execute(query);
      if (Array.isArray(nfRows) && nfRows.length) break;
    }
    const nota = (nfRows && nfRows[0]) || null;
    cab.SERIE = nota?.SERIE || cab.SERIE || '';
    if (!cab.CPF_CLIENTE && (nota?.CPF || nota?.CPFNANOTA)) {
      cab.CPF_CLIENTE = nota.CPF || nota.CPFNANOTA;
    }

    let cliente = null;
    const cpfCupom = String(cab.CPF_CLIENTE || '').replace(/\D/g, '');
    if (cpfCupom) {
      const cliRows = await queryService.execute(
        `SELECT NOME, CPF
         FROM televendas.pdvcliente
         WHERE REPLACE(REPLACE(REPLACE(COALESCE(CPF, ''), '.', ''), '-', ''), '/', '') = '${cpfCupom}'
            OR CAST(CPF_LIMPO AS CHAR) = '${cpfCupom}'
         LIMIT 1`);
      cliente = (cliRows && cliRows[0]) || null;
    }
    return {
      cabecalho: cab,
      itens: itensRows || [],
      pagamentos: (pagamentosRows || []).filter((item, index, arr) => {
        const key = [
          item.FORMAPG,
          item.BANDEIRA,
          item.PARCELAS,
          item.VALOR,
          item.NSU,
          item.TID,
          item.PAYMENTID,
          item.NRAUTORIZACAO
        ].map((v) => String(v ?? '').trim()).join('|');
        return arr.findIndex((row) => [
          row.FORMAPG,
          row.BANDEIRA,
          row.PARCELAS,
          row.VALOR,
          row.NSU,
          row.TID,
          row.PAYMENTID,
          row.NRAUTORIZACAO
        ].map((v) => String(v ?? '').trim()).join('|') === key) === index;
      }),
      nota,
      filialDados: (filialRows && filialRows[0]) || null,
      cliente: cliente || (cpfCupom ? { NOME: '', CPF: cpfCupom } : null)
    };
  }
};

// Serviço de devolução
export const devolucaoService = {
  buscarProduto: async (codigo, filial) => {
    const query = `
      SELECT
        sceprodu.*,
        sceforne.desconto as descfor,
        sceforne.CDCOMPRADOR,
        sceforne.ABREV as FORNECEDOR
      FROM sceprodu, sceforne
      WHERE (sceprodu.CDPRODU = '${codigo}'
        OR sceprodu.BARRA = '${codigo}'
        OR sceprodu.BAR1 = '${codigo}'
        OR sceprodu.BAR2 = '${codigo}'
        OR sceprodu.BAR3 = '${codigo}'
        OR sceprodu.BAR4 = '${codigo}')
        AND sceprodu.CDFORNE = sceforne.CDFORNE
      LIMIT 1
    `;
    const result = await queryService.execute(query);
    return result && result.length > 0 ? result[0] : null;
  },

  buscarNFEs: async (cdprodu, filial) => {
    const query = `
      SELECT
        a.NRNOTA,
        a.QTD,
        a.DATACONF,
        b.ABREV,
        a.ENDERECO,
        a.CDFORNE,
        a.NRSERIE,
        a.QTDEMB,
        (
          SELECT i.CHAVENFE
          FROM sceitensnfent i
          WHERE i.CDEMP = a.CDEMP
            AND i.CDFIL = a.CDFIL
            AND i.CDFORNE = a.CDFORNE
            AND i.NRNOTA = a.NRNOTA
            AND i.SERIE = a.NRSERIE
          LIMIT 1
        ) AS CHAVENFE,
        (
          SELECT e.VLRUNI
          FROM sceent e
          WHERE e.CDEMP = a.CDEMP
            AND e.CDFIL = a.CDFIL
            AND e.CDFORNE = a.CDFORNE
            AND e.NRNOTA = a.NRNOTA
            AND e.CDPRODU = a.CDPRODU
          LIMIT 1
        ) AS VLRUNI
      FROM sceentconf a
      JOIN sceforne b ON b.CDFORNE = a.CDFORNE
      WHERE a.CDEMP = '1'
        AND a.CDFIL = ${filial}
        AND a.CDPRODU = '${cdprodu}'
      ORDER BY a.DATACONF DESC
      LIMIT 10
    `;
    return queryService.execute(query);
  },

  verificarDevolucao: async (chavenfe, cdprodu) => {
    const query = `
      SELECT *
      FROM sceitensnfent
      WHERE CHAVENFE = '${chavenfe}'
        AND CDPRODU = ${cdprodu}
    `;
    const result = await queryService.execute(query);
    return result && result.length > 0 ? result[0] : null;
  },

  buscarPedidos: async (cdprodu, filial) => {
    const query = `
      SELECT
        a.NRPEDIDO,
        a.QTD,
        a.DATA,
        a.VLRPED,
        b.ABREV,
        b.CDCOMPRADOR
      FROM scepedf a, sceforne b, scepedfc c
      WHERE a.CDEMP = '1'
        AND a.CDFIL = ${filial}
        AND a.CDPRODU = '${cdprodu}'
        AND a.CDFORNE = b.CDFORNE
        AND a.NRPEDIDO = c.NRPEDIDO
      ORDER BY a.DATA DESC
      LIMIT 6
    `;
    return queryService.execute(query);
  }
};

// Serviço de comparativo de balanço (inventário EI/SI)
let _balancoCostFieldCache = null; // { table: 'sceestoq'|'sceprodu', column: string }

const BALANCO_COST_PRIORITY = [
  { table: 'sceestoq', column: 'CUSMED' },
  { table: 'sceestoq', column: 'CUSMEDIO' },
  { table: 'sceestoq', column: 'CUSTMED' },
  { table: 'sceestoq', column: 'CUSTO_MEDIO' },
  { table: 'sceestoq', column: 'CUSMEDI' },
  { table: 'sceprodu', column: 'CUSMED' },
  { table: 'sceprodu', column: 'CUSMEDIO' },
  { table: 'sceprodu', column: 'CUSLIQ' }
];

async function detectarColunaCustoMedio() {
  if (_balancoCostFieldCache) return _balancoCostFieldCache;

  const tables = [...new Set(BALANCO_COST_PRIORITY.map((c) => c.table))];
  const cols = [...new Set(BALANCO_COST_PRIORITY.map((c) => c.column))];

  // MySQL/MariaDB: INFORMATION_SCHEMA + DATABASE() funcionam bem nesse cenário
  const query = `
    SELECT TABLE_NAME, COLUMN_NAME
    FROM INFORMATION_SCHEMA.COLUMNS
    WHERE TABLE_SCHEMA = DATABASE()
      AND TABLE_NAME IN ('${tables.join("','")}')
      AND COLUMN_NAME IN ('${cols.join("','")}')
  `;

  let found = [];
  try {
    found = await queryService.execute(query);
  } catch (e) {
    // Se falhar, usa fallback conhecido no projeto (CUSLIQ)
    _balancoCostFieldCache = { table: 'sceprodu', column: 'CUSLIQ' };
    return _balancoCostFieldCache;
  }

  const foundSet = new Set((found || []).map((r) => `${String(r.TABLE_NAME).toLowerCase()}|${String(r.COLUMN_NAME).toUpperCase()}`));
  const pick = BALANCO_COST_PRIORITY.find((c) => foundSet.has(`${c.table}|${c.column}`));
  _balancoCostFieldCache = pick || { table: 'sceprodu', column: 'CUSLIQ' };
  return _balancoCostFieldCache;
}

export const balancoService = {
  buscarMovimentosInventario: async ({ filial, data }) => {
    const fil = Number(filial);
    if (!fil || !data) return [];

    const costField = await detectarColunaCustoMedio();
    const custoExpr =
      costField.table === 'sceestoq'
        ? `COALESCE(e.${costField.column}, p.CUSLIQ, 0)`
        : `COALESCE(p.${costField.column}, p.CUSLIQ, 0)`;

    const query = `
      SELECT
        k.CDFIL,
        k.DATA,
        k.TIPO,
        k.CDPRODU,
        k.QTD,
        k.ESTOQU,
        p.NOME AS NOME_PRODUTO,
        ${custoExpr} AS CUSTO_MEDIO,
        COALESCE(e.ESTOQ, 0) AS ESTOQUE_ATUAL
      FROM scekarde k
      LEFT JOIN sceprodu p ON p.CDPRODU = k.CDPRODU
      LEFT JOIN sceestoq e ON e.CDPRODU = k.CDPRODU AND e.CDFIL = k.CDFIL
      WHERE k.CDFIL = ${fil}
        AND k.TIPO IN ('EI', 'SI')
        AND k.DATA = '${data}'
      ORDER BY k.CDPRODU
    `;

    return queryService.execute(query);
  }
};

export const inventarioService = {
  processarFoto: async ({ filial, imageFile, itens = [] }) => {
    const formData = new FormData();
    formData.append('filial', String(filial || ''));
    if (imageFile) {
      formData.append('image', imageFile);
    }
    formData.append('detectedItems', JSON.stringify(itens));

    try {
      const response = await apiInventarioProxy.post('/proxy/inventario/from-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data?.success) return response.data.data || [];
      throw new Error(response.data?.error || 'Erro ao processar imagem do inventário');
    } catch (error) {
      throw new Error(error?.response?.data?.error || error?.message || 'Erro ao processar imagem do inventário');
    }
  },

  matchItems: async ({ filial, itens = [] }) => {
    const response = await apiInventarioProxy.post('/proxy/inventario/match-items', {
      filial,
      itens
    });
    if (response.data?.success) return response.data.data || [];
    throw new Error(response.data?.error || 'Erro ao relacionar itens do inventário');
  },

  exportarExcel: async (items = []) => {
    const response = await apiInventarioProxy.post('/proxy/inventario/export-xlsx', { items }, {
      responseType: 'blob'
    });
    return response.data;
  }
};

export const statusLojaService = {
  verificarStatus: async (lojaId) => {
    const response = await apiProxy.post('/verificar_status_loja', { loja_id: lojaId });
    if (response.data?.success) return response.data;
    throw new Error(response.data?.error || 'Erro ao verificar status da loja');
  }
};

export default api;
